import React, { useState, useRef, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import './MenuImageImport.css';

const MenuImageImport = ({ 
  onExtractSuccess, 
  onCancel, 
  apiEndpoint = '/affiliate/extract-menu-from-images',
  imageGenEndpoint = '/affiliate/generate-menu-image'
}) => {
  const [images, setImages] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractionProgress, setExtractionProgress] = useState('');
  const [showCamera, setShowCamera] = useState(false);
  const [stream, setStream] = useState(null);
  
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  // Handle file selection
  const handleFileSelect = useCallback((e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Limit to 5 images
    const newFiles = files.slice(0, 5 - images.length);
    
    if (files.length > newFiles.length) {
      toast.error(`Maximum 5 images allowed. Only added ${newFiles.length} image(s).`);
    }

    // Validate file sizes (10MB max each)
    const validFiles = newFiles.filter(file => {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is too large. Maximum file size is 10MB.`);
        return false;
      }
      return true;
    });

    // Create previews
    const newPreviews = validFiles.map(file => URL.createObjectURL(file));
    
    setImages(prev => [...prev, ...validFiles]);
    setPreviews(prev => [...prev, ...newPreviews]);
    
    // Clear the input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [images.length]);

  // Remove an image
  const removeImage = useCallback((index) => {
    URL.revokeObjectURL(previews[index]);
    setImages(prev => prev.filter((_, i) => i !== index));
    setPreviews(prev => prev.filter((_, i) => i !== index));
  }, [previews]);

  // Start camera
  const startCamera = async () => {
    try {
      // Try with ideal constraints first (prefers back camera on mobile, any camera on desktop)
      let mediaStream;
      try {
        mediaStream = await navigator.mediaDevices.getUserMedia({
          video: { 
            facingMode: { ideal: 'environment' }, // Prefer back camera, but accept any
            width: { ideal: 1920 },
            height: { ideal: 1080 }
          }
        });
      } catch (constraintError) {
        // If that fails, try with minimal constraints
        console.log('Retrying camera with minimal constraints...');
        mediaStream = await navigator.mediaDevices.getUserMedia({
          video: true
        });
      }
      
      setStream(mediaStream);
      setShowCamera(true);
      
      // Wait for video ref to be available
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
        }
      }, 100);
    } catch (error) {
      console.error('Camera access error:', error);
      
      // Provide more specific error messages
      if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        toast.error('No camera found. Please connect a camera or use file upload.');
      } else if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        toast.error('Camera access denied. Please allow camera permissions in your browser settings.');
      } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
        toast.error('Camera is in use by another application. Please close it and try again.');
      } else {
        toast.error('Unable to access camera. Please check permissions or use file upload.');
      }
    }
  };

  // Stop camera
  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setShowCamera(false);
  }, [stream]);

  // Capture photo from camera
  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    
    canvas.toBlob((blob) => {
      if (blob && images.length < 5) {
        try {
          // Create a simple filename - some mobile browsers are strict about this
          const filename = `capture${Date.now()}.jpg`;
          
          // Try to create a File object, fallback to using Blob directly if it fails
          let imageFile;
          try {
            imageFile = new File([blob], filename, { type: 'image/jpeg' });
          } catch (fileError) {
            // Fallback for browsers that don't support File constructor well
            console.log('File constructor failed, using blob with name property');
            imageFile = blob;
            imageFile.name = filename;
          }
          
          const preview = URL.createObjectURL(blob);
          
          setImages(prev => [...prev, imageFile]);
          setPreviews(prev => [...prev, preview]);
          
          toast.success('Photo captured!');
        } catch (error) {
          console.error('Error creating file from captured image:', error);
          toast.error('Failed to process captured image. Try uploading instead.');
        }
      } else if (images.length >= 5) {
        toast.error('Maximum 5 images allowed.');
      }
    }, 'image/jpeg', 0.85);
  }, [images.length]);

  // Helper to upload using XMLHttpRequest (more reliable on Safari/mobile)
  const uploadWithXHR = (formData, token, url) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      xhr.open('POST', url, true);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      
      xhr.onload = function() {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const data = JSON.parse(xhr.responseText);
            resolve(data);
          } catch (e) {
            reject(new Error('Invalid response from server'));
          }
        } else {
          try {
            const errorData = JSON.parse(xhr.responseText);
            reject(new Error(errorData.message || `Upload failed with status ${xhr.status}`));
          } catch (e) {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        }
      };
      
      xhr.onerror = function() {
        reject(new Error('Network error - please check your connection and try again'));
      };
      
      xhr.ontimeout = function() {
        reject(new Error('Request timed out - please try with fewer or smaller images'));
      };
      
      // Set a generous timeout for AI processing (2 minutes)
      xhr.timeout = 120000;
      
      xhr.send(formData);
    });
  };

  // Extract menu data from images
  const handleExtract = async () => {
    if (images.length === 0) {
      toast.error('Please add at least one image');
      return;
    }

    setIsExtracting(true);
    setExtractionProgress('Uploading images...');

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      
      // Append images with explicit filename - helps with mobile browser compatibility
      for (let i = 0; i < images.length; i++) {
        const file = images[i];
        // Ensure we have a proper filename for each file
        const filename = file.name || `image${i + 1}.jpg`;
        
        // On some mobile browsers, we need to explicitly set the filename
        if (file instanceof Blob && !(file instanceof File)) {
          // If it's a Blob without File properties, append with explicit filename
          formData.append('images', file, filename);
        } else {
          formData.append('images', file, filename);
        }
      }

      setExtractionProgress('Analyzing images with AI...');

      const url = `${process.env.REACT_APP_API_URL}${apiEndpoint}`;
      let data;

      // Use XMLHttpRequest for better Safari/mobile compatibility
      // Safari can have issues with fetch for file uploads
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      
      if (isSafari) {
        console.log('Using XMLHttpRequest for Safari compatibility');
        data = await uploadWithXHR(formData, token, url);
      } else {
        // Try fetch first, fallback to XHR if it fails
        try {
          const response = await fetch(url, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            },
            body: formData
          });

          data = await response.json();

          if (!response.ok) {
            throw new Error(data.message || 'Failed to extract menu data');
          }
        } catch (fetchError) {
          // If fetch fails (common on some mobile browsers), try XHR
          if (fetchError.message === 'Load failed' || fetchError.message === 'Failed to fetch') {
            console.log('Fetch failed, falling back to XMLHttpRequest');
            data = await uploadWithXHR(formData, token, url);
          } else {
            throw fetchError;
          }
        }
      }

      setExtractionProgress('Processing complete!');

      // Clean up previews
      previews.forEach(url => URL.revokeObjectURL(url));
      
      // Stop camera if running
      stopCamera();

      // Success - pass data to parent
      toast.success('Menu data extracted successfully!');
      
      // Add confidence info to the extracted data for display
      const extractedData = {
        ...data.menuData,
        _extractionConfidence: data.menuData.confidence
      };
      
      onExtractSuccess(extractedData);
    } catch (error) {
      console.error('Extraction error:', error);
      toast.error(error.message || 'Failed to extract menu data from images');
    } finally {
      setIsExtracting(false);
      setExtractionProgress('');
    }
  };

  // Handle cancel
  const handleCancel = () => {
    // Clean up
    previews.forEach(url => URL.revokeObjectURL(url));
    stopCamera();
    onCancel();
  };

  return (
    <div className="menu-image-import">
      <div className="import-header">
        <h2>
          <i className="fas fa-camera"></i> Import Menu from Images
        </h2>
        <p className="import-description">
          Take photos or upload images of your menu/recipe. Our AI will extract the ingredients, 
          instructions, and other details automatically.
        </p>
      </div>

      {/* Camera View */}
      {showCamera && (
        <div className="camera-container">
          <video 
            ref={videoRef} 
            autoPlay 
            playsInline 
            muted
            className="camera-preview"
          />
          <div className="camera-controls">
            <button 
              type="button"
              className="capture-btn"
              onClick={capturePhoto}
              disabled={images.length >= 5}
            >
              <i className="fas fa-camera"></i> Capture
            </button>
            <button 
              type="button"
              className="close-camera-btn"
              onClick={stopCamera}
            >
              <i className="fas fa-times"></i> Close Camera
            </button>
          </div>
          <canvas ref={canvasRef} style={{ display: 'none' }} />
        </div>
      )}

      {/* Image Upload Area */}
      {!showCamera && (
        <div className="upload-area">
          <div className="upload-options">
            <button 
              type="button"
              className="upload-option-btn camera-btn"
              onClick={startCamera}
              disabled={images.length >= 5}
            >
              <i className="fas fa-camera"></i>
              <span>Take Photo</span>
            </button>
            
            <button 
              type="button"
              className="upload-option-btn file-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={images.length >= 5}
            >
              <i className="fas fa-upload"></i>
              <span>Upload Images</span>
            </button>
          </div>
          
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          
          <p className="upload-hint">
            Supports JPEG, PNG, WebP • Max 10MB per image • Up to 5 images
          </p>
        </div>
      )}

      {/* Image Previews */}
      {previews.length > 0 && (
        <div className="image-previews">
          <h3>Selected Images ({previews.length}/5)</h3>
          <div className="preview-grid">
            {previews.map((preview, index) => (
              <div key={index} className="preview-item">
                <img src={preview} alt={`Preview ${index + 1}`} />
                <button 
                  type="button"
                  className="remove-preview-btn"
                  onClick={() => removeImage(index)}
                  title="Remove image"
                >
                  <i className="fas fa-times"></i>
                </button>
                <span className="preview-number">{index + 1}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Extraction Progress */}
      {isExtracting && (
        <div className="extraction-progress">
          <div className="progress-spinner"></div>
          <p>{extractionProgress}</p>
        </div>
      )}

      {/* Tips Section */}
      <div className="import-tips">
        <h4><i className="fas fa-lightbulb"></i> Tips for best results:</h4>
        <ul>
          <li>Ensure text is clearly visible and in focus</li>
          <li>Good lighting helps with text recognition</li>
          <li>Include images of ingredients list and instructions if on separate pages</li>
          <li>Handwritten recipes work too, but print is easier to read</li>
        </ul>
      </div>

      {/* Action Buttons */}
      <div className="import-actions">
        <button 
          type="button"
          className="cancel-btn"
          onClick={handleCancel}
          disabled={isExtracting}
        >
          Cancel
        </button>
        <button 
          type="button"
          className="extract-btn"
          onClick={handleExtract}
          disabled={images.length === 0 || isExtracting}
        >
          {isExtracting ? (
            <>
              <span className="btn-spinner"></span>
              Extracting...
            </>
          ) : (
            <>
              <i className="fas fa-magic"></i>
              Extract Menu Data
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default MenuImageImport;
